import ConfigDebugModal from 'features/debug/ConfigDebugModal'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Platform, View } from 'react-native'
import Animated from 'react-native-reanimated'
import { router, useLocalSearchParams } from 'expo-router'
import { trpc } from 'api'
import { Collapsible } from '@ui/animated/Collapsible'
import AnimatedEmpty from '@ui/animated/AnimatedEmpty'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import { LoadingFooter } from '@ui/lists/LoadingFooter'
import { StrapiID } from '@ca/common/types'
import EmptyResult from '@ui/components/EmptyResult'
import Page from '@shell/Page'
import Header from '@ui/elements/Header'
import { useActiveDeal } from '@order/hooks/useDeal'
import useBundleType from '@order/hooks/useBundleType'
import { useOrderState } from '@order/hooks/useOrderState'
import { FlashList } from '@shopify/flash-list'
import FrameItem from '@frame/Frame'
import useFlattenInfiniteData from 'hooks/useFlattenInfiniteData'
import { useIsFocused } from '@ui/hooks/useIsFocused'
import { useRefreshByUser } from 'hooks/useRefreshByUser'
import { useCustomer } from 'hooks/useSession'
import cn from '@ui/utils/cn'

import Search from './Input'
import useSaveFrame from '@screens/frame/settings/useSaveFrame'
import ButtonsFixed from '@ui/buttons/ButtonsFixed'
import Segmented from '@ui/elements/Segmented'
import checkRequirements from '@order/utils/checkRequirements'
import { useConfigConditionGroupCheck } from 'features/config/useConfigCondition'
import { useConfigConditionValue } from 'features/config/useConfigConditionValue'
import useOrderValues from '@order/hooks/values/useOrderValues'

export default function FrameSearch() {
  const { t } = useTranslation(['ui', 'frame', 'types', 'deals-types'])
  const text = 'frame:search'
  const textEmpty = 'frame:search.empty'

  const { q } = useLocalSearchParams<{
    q: string
  }>()

  const { bundleType } = useBundleType()
  const orderValues = useOrderValues({ bundleType })
  const activeDeal = useActiveDeal()
  const [selectedCatalog, setSelectedCatalog] = useState<string | null>(null)
  // const [frameId, setFrameId] = useState<StrapiID | null>(null)
  // const [open, setOpen] = useState(false)
  const customer = useCustomer()
  const utils = trpc.useUtils()
  const removeByBundle = useOrderState((s) => s.removeByBundle)

  const checkGroup = useConfigConditionGroupCheck()

  const focused = useIsFocused()

  const {
    data,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isInitialLoading,
    isRefetching,
    isFetching,
    isFetched,
  } = trpc.frames.list.useInfiniteQuery(
    {
      limit: 10,
      query: q || '',
      fcatalog: selectedCatalog ?? undefined,
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.meta.nextCursor
      },
      placeholderData: (v) => v,
      refetchOnWindowFocus: false,
      enabled: focused && !!(q || '').trim(),
    }
  )

  const { data: customerFramesData } =
    trpc.frames.customer.list.useInfiniteQuery(
      {
        limit: 100, // TODO: consider better approach to detect if the frame is saved to the current user (compare with cached and add prop on server level?)
        customer_id: customer?.id?.toString(),
      },
      {
        getNextPageParam: (lastPage) => {
          return lastPage.meta.nextCursor
        },
        enabled: focused && !!customer?.id,
      }
    )

  // const handleOpen = useCallback((id: StrapiID) => {
  //   // if (selected.status === 'out') {
  //   //   return
  //   // }
  //   setFrameId(id)
  //   setOpen(true)
  // }, [])

  // const handleClose = useCallback(() => {
  //   setOpen(false)
  // }, [])

  const handleRefresh = useCallback(() => {
    // Keep only the 1st page
    utils.frames.list.setInfiniteData(
      {
        limit: 10,
        query: '1',
      },
      (data) => ({
        pages: data?.pages.slice(0, 1) ?? [],
        pageParams: data?.pageParams.slice(0, 1) ?? [],
      })
    )

    return refetch()
  }, [refetch, utils])

  const onSave = useCallback(() => {
    removeByBundle(bundleType)
    // router.navigate({
    //   pathname: '/frame/select',
    //   params: { bundleType },
    // })
    // handleClose()
  }, [bundleType, removeByBundle])

  const saveFrame = useSaveFrame({ onSuccess: onSave })

  const { isRefetchingByUser, refetchByUser } = useRefreshByUser(
    handleRefresh,
    isRefetching
  )
  const customerFrames = useFlattenInfiniteData(customerFramesData)
  const frames = useFlattenInfiniteData(data)

  const customerFrameIds = useMemo(() => {
    const set = new Set<StrapiID>()

    customerFrames.forEach((frame) => {
      const id = frame.frame?.id
      if (id !== undefined && id !== null) {
        set.add(id as StrapiID)
      }
    })
    return set
  }, [customerFrames])

  const isLoadings =
    (isFetching || isInitialLoading || isLoading) && !isFetchingNextPage

  const showLoading = isLoadings && !!q
  const showEmpty = !isLoadings && !!q && isFetched
  const showItems = isFetched && !isLoadings

  const styleLoading = useAnimatedOpacity(showLoading)
  const styleEmpty = useAnimatedOpacity(showEmpty)
  const styleItems = useAnimatedOpacity(showItems)

  const handlePress = useCallback(
    (frameId: StrapiID) => {
      const isSaved = customerFrameIds.has(frameId)
      if (isSaved) {
        router.canGoBack() && router.back()
        return
      }
      saveFrame.save({ frameId })
    },
    [saveFrame, customerFrameIds]
  )

  const filterConfigName = useMemo(() => {
    const { valid } = checkGroup('frame:filters')
    return valid[0]?.name
  }, [checkGroup])

  const filters = useConfigConditionValue(filterConfigName, 'frameFilters')

  const filterOptions = useMemo(() => {
    if (!filters) {
      return []
    }
    const checkAllowedCatalog = (catalog: string) => {
      const requirements =
        activeDeal?.frameRequirements?.filter(
          (r) => r.config?.variable === 'frame_attributes_fcatalog'
        ) ?? []

      if (requirements.length === 0) {
        return true
      }

      return requirements.some((requirement) =>
        checkRequirements(
          { requires: [requirement] },
          { frame_attributes_fcatalog: catalog }
        )
      )
    }

    return filters.fcatalog.map((catalogId) => {
      if (catalogId === 'all') {
        return {
          id: 'all',
          label: t('types:fcatalog.all'),
        }
      }
      return {
        id: catalogId,
        label: t(`deals-types:fcatalog.${catalogId.toLowerCase()}`),
        disabled: !checkAllowedCatalog(catalogId),
      }
    })
  }, [t, filters, activeDeal])

  return (
    <>
      <Page stage="frame" list validateStaff>
        <FlashList
          ListHeaderComponent={
            <>
              <Header
                list
                title={t(`${text}.header.title`)}
                desc={t(`${text}.header.desc`)}
              />
              <View className="px-8">
                {/* Check if > 1 because we also have the "All frames" option always displayed */}
                {filterOptions.length > 1 && (
                  <View className="mb-4 flex-row justify-center pb-8">
                    <Segmented
                      value={
                        selectedCatalog
                          ? { id: selectedCatalog, label: '' }
                          : { id: 'all', label: '' }
                      }
                      onChange={(v) => {
                        setSelectedCatalog(v.id === 'all' ? null : v.id)
                      }}
                      options={filterOptions}
                    />
                  </View>
                )}
                <Search isFetching={isLoadings} />
              </View>
              <Collapsible open={showLoading} fullWidth>
                <LoadingFooter style={styleLoading} />
              </Collapsible>
            </>
          }
          ListFooterComponent={isFetchingNextPage ? LoadingFooter : null}
          data={frames}
          numColumns={1}
          keyboardShouldPersistTaps="handled"
          contentContainerClassName="pb-8"
          onEndReachedThreshold={0.25}
          onRefresh={refetchByUser}
          refreshing={isRefetchingByUser}
          progressViewOffset={100}
          extraData={customerFrameIds}
          onEndReached={hasNextPage ? fetchNextPage : undefined}
          renderItem={({ item }) => {
            const isSaved = customerFrameIds.has(item.id)
            return (
              <Animated.View style={styleItems} className={cn('px-8 pt-8')}>
                <FrameItem
                  frame={item}
                  onPress={() => handlePress(item.id)}
                  status={isSaved ? 'saved' : undefined}
                  orderValues={orderValues}
                />
              </Animated.View>
            )
          }}
          ListEmptyComponent={
            <AnimatedEmpty style={styleEmpty}>
              <EmptyResult
                title={t(`${textEmpty}.result.title`)}
                desc={t(`${textEmpty}.result.desc`)}
                button={
                  Platform.OS === 'web'
                    ? undefined
                    : {
                        label: t('ui:search'),
                        variant: 'outlined',
                        onPress: () => router.setParams({ q: '' }),
                      }
                }
              />
            </AnimatedEmpty>
          }
        />
      </Page>
      <ButtonsFixed
        // hidden={customerFrameIds.size === 0}
        buttons={[
          {
            variant: 'outlined',
            label: t(`frame:select.header.title`),
            iconStart: 'arrow-narrow-left',
            onPress: () => {
              router.dismissTo(`/frame/select?bundleType=${bundleType}`)
            },
          },
        ]}
      />
      <ConfigDebugModal />
    </>
  )
}
